import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Stack,
  Button,
  Text,
  Icon,
  Divider,
  IconButton,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType;
  onClick: () => void;
}

interface CollapsibleLeftNavProps {
  title: string;
  items: NavItem[];
  activeItemId: string;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}

const CollapsibleLeftNav: React.FC<CollapsibleLeftNavProps> = ({
  title,
  items,
  activeItemId,
  defaultWidth = 200,
  minWidth = 180,
  maxWidth = 350,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [width]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startXRef.current;
    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isResizing, minWidth, maxWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Box
      ref={navRef}
      bg="bg.surface"
      boxShadow="lg"
      borderRadius="lg"
      overflowY="hidden"
      width={isCollapsed ? "60px" : `${width}px`}
      minWidth={isCollapsed ? "60px" : `${minWidth}px`}
      maxWidth={isCollapsed ? "60px" : `${maxWidth}px`}
      transition={isResizing ? "none" : "width 0.3s ease"}
      position="relative"
    >
      <Box
        borderColor="#4e1e6c"
        height="100%"
        display="flex"
        flexDirection="column"
      >
        <Box bg="bg.surface" p={2} height="100%">
          {/* Header with title and collapse button */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {!isCollapsed && (
              <Text
                fontWeight="bold"
                color="fg.muted"
                fontSize="sm"
                flex="1"
                mb={0}
              >
                {title}
              </Text>
            )}
            <IconButton
              aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
              icon={isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              size="xs"
              variant="ghost"
              onClick={toggleCollapse}
              _hover={{ bg: "purple.100" }}
            />
          </Box>

          {!isCollapsed && <Divider mb={3} mt={2} />}

          <Stack
            height="calc(100% - 60px)"
            spacing="1"
            alignItems="left"
            overflowY="auto"
            sx={{
              "::-webkit-scrollbar": { width: "4px" },
              "::-webkit-scrollbar-track": { background: "gray.100" },
              "::-webkit-scrollbar-thumb": {
                background: "gray.500",
                borderRadius: "8px"
              },
              "::-webkit-scrollbar-thumb:hover": { background: "gray.600" },
              scrollbarWidth: "thin",
              scrollbarColor: "gray.500 gray.100",
            }}
          >
            {items.map((item) => (
              <Button
                key={item.id}
                variant="tertiary"
                justifyContent={isCollapsed ? "center" : "start"}
                iconSpacing="3"
                leftIcon={!isCollapsed ? <Icon as={item.icon} boxSize="1.2rem" /> : undefined}
                _hover={{ bg: "purple.100", color: "black" }}
                bg={activeItemId === item.id ? "purple.600" : "white"}
                color={activeItemId === item.id ? "white" : "fg.default"}
                borderRadius="lg"
                py={2}
                onClick={item.onClick}
                cursor="pointer"
                title={isCollapsed ? item.label : undefined}
              >
                {isCollapsed ? (
                  <Icon as={item.icon} boxSize="1.2rem" />
                ) : (
                  <Text fontSize="sm" fontWeight="medium" align="left" m={0}>
                    {item.label}
                  </Text>
                )}
              </Button>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Resize handle */}
      {!isCollapsed && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          width="4px"
          bg="transparent"
          cursor="col-resize"
          _hover={{ bg: "purple.200" }}
          onMouseDown={handleMouseDown}
        />
      )}
    </Box>
  );
};

export default CollapsibleLeftNav;
